/***************************************************************************************************
** useFormSearch
***************************************************************************************************/

import { useMemo, useState, useCallback } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { sort } from "@utils";

const SEARCH_STATE_VALUES = {
  NOT_SEARCHABLE:  Symbol("not_searchable"),
  SEARCHING:       Symbol("searching"),
  SEARCHED:        Symbol("searched"),
  SEARCH_CLEARING: Symbol("search_clearing"),
  SEARCH_CLEARED:  Symbol("search_cleared"),
};

const defaultProps = {
  params: {},
  baseParams: {},
  defaultSortEntries: [],
  setParams: (() => {}),
  searchFields: [],
  fetchRecords: (() => {}),
};

export default function useFormSearch(props) {
  const {
    params,
    baseParams,
    defaultSortEntries,
    setParams,
    searchFields,
    fetchRecords,
  } = { ...defaultProps, ...props };

  const searchFieldKeys = useMemo(() => searchFields.map((field) => field.name), [searchFields]);

  /***************************************************************************************************
  ** Search State
  ***************************************************************************************************/

  const initialSearchStateValue = useMemo(() => {
    if (!searchFields.length > 0) {
      return SEARCH_STATE_VALUES.NOT_SEARCHABLE;
    } else if (searchFields.find((field) => params[field.name])) {
      return SEARCH_STATE_VALUES.SEARCHED;
    } else {
      return SEARCH_STATE_VALUES.SEARCH_CLEARED;
    }
  }, [params, searchFields]);

  const [searchStateValue, setSearchStateValue] = useState(initialSearchStateValue);

  const isSortChanged = useMemo(() => {
    const activeSortEntries = sort.getEntriesFromParams(params);
    return !sort.areEntriesEqual(activeSortEntries, defaultSortEntries);
  }, [params.s, defaultSortEntries]);

  const searchState = useMemo(() => {
    const state = {
      value: searchStateValue,
      isNotSearchable:  (searchStateValue === SEARCH_STATE_VALUES.NOT_SEARCHABLE),
      isSearching:      (searchStateValue === SEARCH_STATE_VALUES.SEARCHING),
      isSearched:       (searchStateValue === SEARCH_STATE_VALUES.SEARCHED),
      isSearchClearing: (searchStateValue === SEARCH_STATE_VALUES.SEARCH_CLEARING),
      isSearchCleared:  (searchStateValue === SEARCH_STATE_VALUES.SEARCH_CLEARED),
    };

    state.isClearable = state.isSearched || isSortChanged;

    return state;
  }, [searchStateValue, isSortChanged]);

  /***************************************************************************************************
  ** Search Params
  ***************************************************************************************************/

  const [searchParams, setSearchParams] = useState(params);
  const setSearchParam = (name, value) => setSearchParams((values) => ({ ...values, [name]: value }));

  const handleSearchParamChange = useCallback((event) => {
    const { name, value } = event.target;
    setSearchParam(name, value);
  }, []);

  /***************************************************************************************************
  ** Handlers
  ***************************************************************************************************/

  const handleSearch = useCallback((event) => {
    event.preventDefault();

    setSearchStateValue(SEARCH_STATE_VALUES.SEARCHING);

    setParams((currentParams) => {
      const newParams = {
        ...currentParams,
        ...searchParams,
        page: 1,
      };

      if (currentParams.s !== undefined && currentParams.s !== null) {
        newParams.s = currentParams.s;
      }

      return newParams;
    });

    fetchRecords(searchParams).then(() => {
      setSearchStateValue(SEARCH_STATE_VALUES.SEARCHED);
    }).catch(() => {});
  }, [fetchRecords, setParams, searchParams]);

  const handleSearchClear = useCallback((_event, _handler) => {
    if (searchState.isClearable) {
      const newSearchParams = Object.keys(searchParams).reduce((result, key) => {
        if (!searchFieldKeys.includes(key)) {
          result[key] = searchParams[key];
        } else if (baseParams[key]) {
          result[key] = baseParams[key];
        }
        return result;
      }, {});

      const defaultSortParam = sort.serializeEntries(defaultSortEntries);

      if (defaultSortParam) {
        newSearchParams.s = defaultSortParam;
      } else {
        delete newSearchParams.s;
      }

      setSearchStateValue(SEARCH_STATE_VALUES.SEARCH_CLEARING);
      setSearchParams(newSearchParams);
      setParams({ ...newSearchParams, page: 1 });

      fetchRecords(newSearchParams).then(() => {
        setSearchStateValue(SEARCH_STATE_VALUES.SEARCH_CLEARED);
      }).catch(() => {});

      document.activeElement.blur();
    }
  }, [
    searchFieldKeys,
    searchParams,
    baseParams,
    defaultSortEntries,
    setParams,
    searchState.isClearable,
    fetchRecords,
  ]);

  useHotkeys("escape", handleSearchClear, {
    enabled: true,
    enableOnFormTags: ["INPUT", "TEXTAREA", "SELECT"],
    enableOnContentEditable: true,
    preventDefault: true,
  }, [handleSearchClear]);

  /**************************************************************************************************/

  return {
    searchState,
    searchParams,
    handleSearchParamChange,
    handleSearch,
    handleSearchClear,
  };
}
